import { counts, FILTERS } from './filters';
import { STATIONS } from './stations';

/**
 * The filter control: a closed disclosure that stays out of the way until asked
 * for, but states plainly how many stations it is hiding once active.
 *
 * A quiet filter that silently shortens the list is worse than no filter, so the
 * summary carries the count and a reset sits beside it whenever anything is on.
 */
export function renderFilterPanel(
  host: HTMLElement,
  active: Set<string>,
  onChange: () => void,
): void {
  const shown = STATIONS.filter((s) =>
    FILTERS.every((f) => !active.has(f.key) || f.matches(s)),
  ).length;

  const optionCounts = counts(active);
  const isOn = active.size > 0;

  // Preserve open/closed across re-renders so toggling a box does not collapse it.
  const wasOpen = host.querySelector('details')?.open ?? false;

  host.innerHTML = `
    <details class="filter" ${wasOpen ? 'open' : ''}>
      <summary>
        <span class="filter-label">Filter</span>
        ${isOn
          ? `<span class="filter-count">${shown} of ${STATIONS.length}</span>`
          : ''}
      </summary>
      <div class="filter-body">
        ${FILTERS.map((f) => {
          const n = optionCounts[f.key];
          const on = active.has(f.key);
          // A choice leading nowhere is disabled rather than silently empty.
          const dead = n === 0 && !on;
          return `
            <label class="filter-option${dead ? ' is-empty' : ''}">
              <input type="checkbox" value="${f.key}" ${on ? 'checked' : ''} ${dead ? 'disabled' : ''}>
              <span>${f.label}</span>
              <span class="filter-n">${n}</span>
            </label>`;
        }).join('')}
        ${isOn ? '<button type="button" class="filter-clear">Clear filters</button>' : ''}
      </div>
    </details>`;

  host.querySelectorAll<HTMLInputElement>('.filter-option input').forEach((box) => {
    box.addEventListener('change', () => {
      if (box.checked) active.add(box.value);
      else active.delete(box.value);
      onChange();
    });
  });

  host.querySelector<HTMLButtonElement>('.filter-clear')?.addEventListener('click', () => {
    active.clear();
    onChange();
  });
}
